import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import PageBanner from "../components/PageBanner";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import { company } from "../mock";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";

const fieldCls =
  "bg-white border-brand-line rounded-none text-brand-ink placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-brand-accent h-12";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted mb-2.5 block";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Eksik bilgi", description: "Lütfen ad, e-posta ve mesaj alanlarını doldurun." });
      return;
    }
    setSending(true);
    setTimeout(() => {
      const saved = JSON.parse(localStorage.getItem("ilka_contact") || "[]");
      saved.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem("ilka_contact", JSON.stringify(saved));
      setSending(false);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast({ title: "Mesajınız alındı", description: "En kısa sürede sizinle iletişime geçeceğiz." });
    }, 700);
  };

  const info = [
    { icon: MapPin, title: "Adres", value: company.location },
    { icon: Phone, title: "Telefon", value: company.phone, href: company.phoneHref },
    { icon: Mail, title: "E-posta", value: company.email, href: `mailto:${company.email}` },
    { icon: Clock, title: "Çalışma Saatleri", value: `${company.hoursWeek} · ${company.hoursSunday}` }
  ];

  return (
    <div className="bg-white" data-testid="contact-page">
      <PageBanner
        title="İletişim"
        subtitle="Projeleriniz için bize ulaşın, en kısa sürede dönüş yapalım."
        breadcrumbs={[{ label: "İletişim" }]}
        image="/images/slider/slider5.jpg"
      />
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <SectionHeading eyebrow="İletişim Bilgilerimiz" title="Bize Ulaşın" />
            <p className="mt-6 text-[14.5px] text-brand-muted leading-[1.85] font-light">{company.about}</p>
            <div className="mt-10 space-y-px bg-brand-line border border-brand-line">
              {info.map((it, i) => {
                const Icon = it.icon;
                const content = (
                  <div className="bg-white flex items-start gap-5 px-6 py-5 hover:bg-brand-paper transition-colors duration-300">
                    <Icon className="w-4 h-4 text-brand-accent mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-brand-muted">{it.title}</div>
                      <div className="mt-1.5 text-[14px] text-brand-ink break-all">{it.value}</div>
                    </div>
                  </div>
                );
                return it.href ? (
                  <a key={i} href={it.href} className="block" data-testid={`contact-info-${i}`}>
                    {content}
                  </a>
                ) : (
                  <div key={i} data-testid={`contact-info-${i}`}>
                    {content}
                  </div>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={150} className="lg:col-span-7">
            <form onSubmit={onSubmit} data-testid="contact-form" className="bg-brand-paper border border-brand-line p-8 md:p-12">
              <div className="eyebrow text-brand-accent">İletişim Formu</div>
              <h3 className="mt-4 font-display text-xl md:text-2xl font-light text-brand-ink tracking-[-0.03em]">
                Projenizi bize anlatın
              </h3>
              <div className="mt-10 grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Ad - Soyad *</label>
                  <Input name="name" data-testid="contact-name-input" value={form.name} onChange={onChange} placeholder="Adınız Soyadınız" className={fieldCls} />
                </div>
                <div>
                  <label className={labelCls}>Telefon</label>
                  <Input name="phone" data-testid="contact-phone-input" value={form.phone} onChange={onChange} placeholder="Telefon numaranız" className={fieldCls} />
                </div>
              </div>
              <div className="mt-6">
                <label className={labelCls}>E-posta Adresi *</label>
                <Input name="email" type="email" data-testid="contact-email-input" value={form.email} onChange={onChange} placeholder="ornek@mail.com" className={fieldCls} />
              </div>
              <div className="mt-6">
                <label className={labelCls}>Mesajınız *</label>
                <Textarea
                  name="message"
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={onChange}
                  rows={5}
                  placeholder="Mesajınızı yazın..."
                  className="bg-white border-brand-line rounded-none text-brand-ink placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-brand-accent"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-btn"
                className="mt-8 group font-mono text-[11px] uppercase tracking-[0.18em] bg-brand-accent hover:bg-brand-accent-dark disabled:opacity-60 text-white pl-6 pr-3.5 py-3.5 rounded-full flex items-center gap-3 transition-colors duration-300"
              >
                {sending ? "Gönderiliyor..." : "Mesaj Gönder"}
                <span className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <section className="h-[420px] w-full border-t border-brand-line grayscale-[0.35] hover:grayscale-0 transition-[filter] duration-700">
        <iframe
          title="Harita"
          src="https://www.google.com/maps?q=Kartepe,+Kocaeli&output=embed"
          className="w-full h-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>
    </div>
  );
}
